<template>
  <div v-if="content" class="rich-text" v-html="content" />
</template>

<script>
// Contentful
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';

import { mapGetters } from 'vuex';

// Utils
import { toSlug } from 'utils/string';

export default {
  name: 'module-rich-text',

  // Components
  // Props
  props: {
    entry: {
      type: Object,
      default: () => ({}),
    },
  },

  // Data
  // Computed
  computed: {
    ...mapGetters('contentful', ['article', 'topic', 'topics']),
    ...mapGetters('app', ['org']),

    content() {
      const heading = (level, heading) =>
        `<h${level} class="h${level}" id="${toSlug(
          heading
        )}">${heading}</h${level}>`;

      const createCallOut = (name, description) => `
        <div class="callout">
          <span 
          class="callout__title c-black fw-bold ">${name}</span>
          <span class="callout__description">${description}</span>
        </div>
      `;

      const createQuote = (quote, author) => `
        <blockquote class="quote ml-auto mr-auto">
          <strong class="quote__title">${quote}</strong>
          <b class="quote__author">${author}</b>
        </blockquote>
      `;

      const createImage = (file, title, description, height, width) => {
        const alt = `${title || ''} ${description ? `· ${description}` : ''}`;
        const img = `<img class="figure__image" src="${file.url}" alt="${alt ||
          ''}"${height ? ` height="${height}"` : ''}${
          width ? ` width="${width}"` : ''
        } title="${alt || ''}" />`;
        const caption = title
          ? `<figcaption class="figure__caption small">${title}</figcaption>`
          : '';

        return `<figure class="figure">${img}${caption}</figure>`;
      };

      const createVideo = (file, height, width) => `
        <video class="video mt mb" ${height ? ` height="${height}"` : ''}${
  width ? ` width="${width}"` : ''
} controls controlsList="nodownload">
          <source src="${file.url}" type="video/mp4">
          <source src="${file.url}" type="video/ogg">
          Your browser does not support the video tag.
        </video>
      `;

      const createAudio = file => `
        <audio class="audio mt mb" controls controlsList="nodownload">
          <source src="${file.url}" type="audio/ogg">
          <source src="${file.url}" type="audio/mpeg">
          Your browser does not support the audio element.
        </audio>
      `;

      const createFile = file => `
        <a class="link mt" href="${file.url}" target="_blank" style="display: flex; ">
          <svg xmlns="http://www.w3.org/2000/svg" width="25px" height="25px" xml:space="preserve" viewBox="0 0 303.19 303.19"><path fill="#e8e8e8" d="M219.82 0H32.84v303.19h237.51V50.52z"/><path fill="#fb3449" d="M230.01 149.94c-3.64-6.5-16.23-8.54-22-9.46-4.55-.72-9.2-.94-13.8-.93a168 168 0 0 0-10.7.35c-1.3.09-2.58.2-3.86.31a93.6 93.6 0 0 1-3.81-4.2c-7.82-9.26-14.14-19.75-19.28-30.66a121.06 121.06 0 0 0 3.12-16.49c1.2-10.43 1.62-22.3-2.3-32.25-1.34-3.43-4.94-7.6-9.09-5.53-4.77 2.4-6.11 9.17-6.5 13.98-.31 3.88-.1 7.77.56 11.59.66 3.84 1.73 7.5 2.9 11.14 1.08 3.34 2.28 6.66 3.58 9.94a171.6 171.6 0 0 1-2.63 7.6c-2.15 5.65-4.48 11-6.71 16.17l-3.47 7.5a254.37 254.37 0 0 1-11.81 23.02c-10.17 3.59-19.29 7.74-26.86 12.63a58.72 58.72 0 0 0-10.64 8.6c-2.82 2.95-5.7 6.78-5.94 11.02-.14 2.4.8 4.72 2.77 6.14 2.7 2.02 6.27 1.88 9.4 1.23 10.25-2.15 18.12-10.96 24.82-18.4a202.39 202.39 0 0 0 15.37-19.46l.04-.05c9.42-2.92 19.68-5.4 30.57-7.2 4.98-.83 10.09-1.5 15.3-1.98a72.05 72.05 0 0 0 11.93 9.17 54.85 54.85 0 0 0 10.69 5.11 76.56 76.56 0 0 0 11.53 2.89c1.98.28 4.01.41 6.09.34 4.63-.18 11.28-1.96 11.71-7.57a7.84 7.84 0 0 0-.98-4.56zm-110.87 10.3a170.83 170.83 0 0 1-6.23 9.05c-4.82 6.56-10.34 14.37-18.32 17.28a14.17 14.17 0 0 1-5.62 1c-1.87-.1-3.72-.93-3.63-3.06a9 9 0 0 1 1.42-3.93 24.28 24.28 0 0 1 3.27-4.23 54.36 54.36 0 0 1 9.78-7.92c5.87-3.8 12.87-7.23 20.67-10.28l-1.34 2.1zm27.23-84.26c-.6-3.5-.7-7.07-.33-10.5.19-1.71.54-3.38 1.04-4.95.43-1.33 1.35-4.58 2.83-5 2.43-.68 3.17 4.54 3.45 6.01 1.56 8.4.18 17.74-1.7 25.97-.3 1.31-.63 2.6-.97 3.88-.58-1.6-1.13-3.2-1.65-4.82-1.1-3.52-2.1-7.09-2.67-10.58zm16.68 66.28a236.5 236.5 0 0 0-25.98 5.71c.98-.27 5.47-8.79 6.48-10.55a128.61 128.61 0 0 0 11.35-26.2c4.9 9.7 10.85 18.96 18.16 27.21.67.75 1.35 1.5 2.05 2.22-4.1.45-8.12.98-12.06 1.61zm61.74 11.7c-.33 1.8-4.19 2.84-5.98 3.12a34.3 34.3 0 0 1-16.03-1.54 51.36 51.36 0 0 1-10.06-4.69 59.68 59.68 0 0 1-8.94-6.67c3.43-.2 6.9-.34 10.4-.28 3.48.04 7 .21 10.47.67 6.5.72 13.8 2.96 18.93 7.18 1 .84 1.33 1.57 1.21 2.2zM227.64 25.26H32.84V0h186.98z"/><g fill="#a4a9ad"><path d="M126.84 241.15c0 5.36-1.58 9.5-4.74 12.42s-7.65 4.38-13.47 4.38h-3.65v15.92H92.02V225.9h16.6c6.07 0 10.62 1.33 13.66 3.97 3.04 2.65 4.56 6.41 4.56 11.3zm-21.86 6.24h2.37c1.95 0 3.5-.55 4.64-1.64 1.15-1.1 1.73-2.6 1.73-4.53 0-3.24-1.8-4.86-5.39-4.86H105v11.03zM175.22 248.86c0 8.01-2.21 14.18-6.62 18.51s-10.6 6.5-18.59 6.5H134.5V225.9h16.6c7.7 0 13.65 1.97 17.84 5.9 4.19 3.95 6.28 9.64 6.28 17.07zm-13.46.46c0-4.4-.87-7.65-2.6-9.78-1.75-2.12-4.39-3.18-7.94-3.18h-3.77v26.88h2.89c3.94 0 6.83-1.15 8.66-3.43 1.84-2.29 2.76-5.78 2.76-10.49zM196.58 273.87H183.8V225.9h28.36v10.4h-15.6v9.16h14.38v10.4h-14.37v18.02z"/></g><path fill="#d1d3d3" d="M219.82 50.52h50.53L219.82 0z"/></svg>
          <span style="margin-left: 10px; margin-top: 3px;">${file.fileName}</span>
        </a>
      `;

      const createLink = (uri, content, isAnchor) => {
        if (isAnchor) {
          return `
            <a class="anchor" id="${uri}">${content}</a>
          `;
        }

        return `
          <a class="link c-black" href="${uri}" target="_blank">${content}</a>
        `;
      };

      const createCodeBlock = string => {
        const doc = new DOMParser().parseFromString(string, 'text/html');
        const parseDoc = doc.documentElement.textContent;
        const fragment = new DocumentFragment();
        return (fragment.innerHTML = parseDoc);
      };

      const options = {
        renderMark: {
          [MARKS.BOLD]: text => `<strong>${text}</strong>`,
          [MARKS.CODE]: text => createCodeBlock(text),
        },
        renderNode: {
          [INLINES.ASSET_HYPERLINK]: (node, next) =>
            `<a href="/${node.data.target.fields.file.url}">${next(
              node.content
            )}</a>`,

          [INLINES.ENTRY_HYPERLINK]: (node, next) =>
            `<a href="/${node.data.target.fields.slug}">${next(
              node.content
            )}</a>`,
          [INLINES.HYPERLINK]: ({ data, content }) => {
            if (!content.length) return;

            return createLink(
              data.uri,
              content[0].value,
              data.uri.startsWith('#')
            );
          },
          [INLINES.EMBEDDED_ENTRY]: node => {
            const item = node.data.target;
            const articleId = item.sys.id;
            const articleName = item.fields && item.fields.name;
            const topicId = item.fields.topics && item.fields.topics[0].sys.id;
            const contentType = item.sys.contentType
              ? item.sys.contentType.sys.id
              : 'articles';

            if (topicId && topicId.length > 0) {
              return `<blockquote class="mb db w100"><a class="link link--no-underline" href="/topics/${toSlug(
                this.topic && this.topic(topicId) && this.topic(topicId).name
              )}/${toSlug(
                articleName
              )}?topicId=${topicId}&articleId=${articleId}"><h5 class="ttu c-mid" style="margin-bottom: 10px">${contentType.toUpperCase()}</h5>
                  <h3 class="h5 mt0 mb0 c-black">${articleName}</h3></a></blockquote>`;
            }
            

            return;
          },
          [BLOCKS.HEADING_1]: (node, next) => heading(2, next(node.content)),
          [BLOCKS.HEADING_2]: (node, next) => heading(2, next(node.content)),
          [BLOCKS.HEADING_3]: (node, next) => heading(3, next(node.content)),
          [BLOCKS.HEADING_4]: (node, next) => heading(4, next(node.content)),
          [BLOCKS.HEADING_5]: (node, next) => heading(5, next(node.content)),
          [BLOCKS.HEADING_6]: (node, next) => heading(6, next(node.content)),
          [BLOCKS.PARAGRAPH]: (node, next) =>
            `<p class="p">${next(node.content)}</p>`,
          [BLOCKS.OL_LIST]: (node, next) =>
            `<ol class="list list--ol p">${next(node.content)}</ol>`,
          [BLOCKS.UL_LIST]: (node, next) =>
            `<ul class="list list--ul p">${next(node.content)}</ul>`,
          [BLOCKS.HR]: () => '<hr class="hr" />',
          [BLOCKS.QUOTE]: (node, next) =>
            `<blockquote class="blockquote">${next(node.content)}</blockquote>`,
          [BLOCKS.EMBEDDED_ENTRY]: ({ data }) => {
            const { target } = data;
            const { name, description, quote, excerpt, author } = target.fields;
            const contentType = target.sys.contentType
              ? target.sys.contentType.sys.id
              : 'topics';
            const topicId =
              target.fields.topics && target.fields.topics[0].sys.id;
            const articleId = target.sys.id;


            if (contentType === 'topics' && articleId && articleId.length > 0) {
              return `
                <blockquote class="mb db w100">
                <a class="link link--no-underline" href="/topics/${toSlug(articleId.length > 0 && 
    this.topic(articleId) && this.topic(articleId).name
  )}?topicId=${articleId}">
                  <h5 class="ttu c-mid" c-black style="margin-bottom: 10px">Topic</h5>
                  <h3 class="h5 mt0 mb0 c-black">${name}</h3>
                  ${
  excerpt && excerpt.length > 0
    ? `<p class="mt0 mb0 c-black">${excerpt}</p>`
    : ''
}
</a>
                </blockquote>
              `;
            }

            if (contentType === 'articles' && topicId.length > 0)
              return `
              <blockquote class="mb db w100"><a class="link link--no-underline" href="/topics/${toSlug(
    topicId.length > 0 && this.topic(topicId) && this.topic(topicId).name
  )}/${toSlug(name)}?topicId=${topicId}&articleId=${articleId}">
<h5 class="ttu c-mid" style="margin-bottom: 10px">Article</h5>
<h3 class="h5 mt0 mb0 c-black">${name}</h3>
              ${
  excerpt && excerpt.length > 0
    ? `<p class="mt0 mb0">${excerpt}...</p>`
    : ''
}
</a></blockquote>
            `;

            if (contentType === 'callout')
              return createCallOut(name, description);
            if (contentType === 'quote') return createQuote(quote, author);
          },
          [BLOCKS.EMBEDDED_ASSET]: ({ data }) => {
            if (!data) return '';

            const target = data.target || {};
            const fields = target.fields || {};
            const file = fields.file || {};

            if (file.url && file.contentType === 'application/pdf') {
              return createFile(file);
            }

            if (file.url && /^video\//.test(file.contentType))
              return createVideo(file);

            if (file.url && /^audio\//.test(file.contentType))
              return createAudio(file);

            if (file.url && /^image\//.test(file.contentType)) {
              const { height, width } = (file.details || {}).image || {};
              const title = fields.title;
              const description = fields.description;

              return createImage(file, title, description, height, width);
            }
          },
        },
      };

      return documentToHtmlString(this.entry, options);
    },
  },

  // Lifecycles
  updated() {
    const anchorLinks = document.getElementsByClassName('anchor');

    _.forEach(anchorLinks, link => {
      // eslint-disable-line no-undef
      link.addEventListener('click', () => {
        this.$scrollTo(link.id, 550, { offset: -100 });
      });
    });
  },

  destroyed() {
    const anchorLinks = document.getElementsByClassName('anchor');

    _.forEach(anchorLinks, link => {
      // eslint-disable-line no-undef
      link.removeEventListener('click', {});
    });
  },
};
</script>



// WEBPACK FOOTER //
// RichText.vue?adac3918